<template>
	<main class="pc-square">
		<div class="main pc-center-1200" :aria-busy="pageDataPending">
			<PageSkeleton v-if="showPageSkeleton" />
			<template v-else>
				<!-- <div class="pc-square-toolbar">
				<SquareSearchBar class="pc-square-toolbar__search" trigger-only @open="openSearchModal" />

				<button class="pc-square-toolbar__creator-btn" type="button" @click="openCreatorModal">
					{{ t("square.index.creatorCenterBtn") }}
				</button>
			</div> -->

				<!-- 播客 -->

				<section>
					<Recommend />
				</section>

				<!-- <div v-if="displayBanners.length" class="banner" :ref="bindBannerRef">
				<SquareBannerCarousel
					:slides="displayBanners"
					:locale="locale"
					@banner-click="handleBannerClick"
				/>
			</div> -->

				<!-- Shorts -->

				<section>
					<Shorts />
				</section>

				<!-- 圖文 -->
				<section>
					<Hot :isIndexType="true" />
				</section>
			</template>
		</div>

		<PcSearchModal
			v-model:show="showSearchModal"
			search-tracking-scope="media_square"
			trigger-position="square_home_search"
		/>
	</main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from "vue";

import { storeToRefs } from "pinia";

import { getSquareBanner, playContent } from "~/api/api";

import { removeDomain } from "~/utils/common";

import { useSquarePageData } from "@variants/seer/square/composables/useSquarePageData";

import { useUserStore } from "~/stores/user";

import { useI18n } from "~/composables/useI18n";

import SquareSearchBar from "~/components/pc/square-search-bar/index.vue";

import PcSearchModal from "~/components/pc/header/pc-search-modal.vue";

import { useAppDownloadModal } from "~/composables/useAppDownloadModal";

import SquareBannerCarousel from "./components/banner-carousel/index.vue";

import Recommend from "./components/recommend/index.vue";

import Shorts from "./components/shorts/index.vue";

import Hot from "./components/hot/index.vue";

import PageSkeleton from "./components/page-skeleton/index.vue";

import {
	COLD_START_TAGS_CONFIRMED_EVENT,
	COLD_START_TAGS_TAB_SWITCH_EVENT,
} from "~/utils/coldStartTags";

import eventBus from "~/utils/eventBus";

import {
	MEDIA_PAGE_NAMES,
	useMediaBannerImpression,
	useMediaSquareTracking,
} from "../composables/useMediaSquareTracking";

import dayjs from "dayjs";

import duration from "dayjs/plugin/duration";

import relativeTime from "dayjs/plugin/relativeTime";

import "dayjs/locale/zh-tw";

import "dayjs/locale/en";

const STORAGE_KEY = "square_pc_scroll_top";

dayjs.extend(relativeTime);

dayjs.extend(duration);

const {
	data: pageData,
	refresh: refreshPageData,
	pending: pageDataPending,
} = useSquarePageData("pc");

const showPageSkeleton = computed(
	() =>
		pageDataPending.value &&
		!(pageData.value?.shorts?.length || pageData.value?.recommend?.podcastTopSix?.length)
);

const { locale, t } = useI18n();

const showSearchModal = ref(false);

const { openAppDownloadModal } = useAppDownloadModal();

const mediaTracking = useMediaSquareTracking({
	deviceType: "pc",

	pageName: MEDIA_PAGE_NAMES.HOME,
});

const { bindBannerRef } = useMediaBannerImpression({ track: mediaTracking.track });

provide("mediaSquareTracking", mediaTracking);

const { data: bannerList } = await useLazyAsyncData(
	"square-pc-banner",

	async () => {
		const res = await getSquareBanner({
			page: 1,

			is_media: 1,
		});

		const list = res?.data?.top_banner;

		return Array.isArray(list) ? list : [];
	},

	{
		default: () => [],

		server: true,
	}
);

const displayBanners = computed(() => bannerList.value || []);

const resolveBannerUrl = item => {
	const raw = item?.actioncontent?.global_type || "";

	if (!raw) return "";

	const deeplinkMatch = raw.match(/^comxylling:\/\/(.+)$/i);

	const url = deeplinkMatch ? deeplinkMatch[1] : raw;

	if (/^https?:\/\//i.test(url)) {
		return url;
	}

	if (url.startsWith("/")) {
		return url;
	}

	return removeDomain(url, 1) || url;
};

const handleBannerClick = (item, index) => {
	const targetUrl = resolveBannerUrl(item);

	mediaTracking.trackBannerClick({
		bannerId: String(item?.id || "media_square_promo"),

		targetUrl,
	});

	if (!targetUrl) return;

	if (/^https?:\/\//i.test(targetUrl)) {
		window.open(targetUrl, "_blank");

		return;
	}

	navigateTo(targetUrl);
};

const openSearchModal = () => {
	showSearchModal.value = true;
};

const openCreatorModal = () => {
	mediaTracking.trackCreatorCenterClick("media_square_home_toolbar");

	openAppDownloadModal();
};

const getDayjsLocale = () => (locale.value === "en" ? "en" : "zh-tw");

const formatFunction = seconds => {
	dayjs.locale(getDayjsLocale());

	return dayjs.duration(seconds, "seconds").format("mm:ss");
};

const formatDate = timestamp => {
	dayjs.locale(getDayjsLocale());

	const now = dayjs();

	const targetDate = dayjs.unix(timestamp);

	const diffInDays = now.diff(targetDate, "day");

	if (diffInDays <= 3) {
		return targetDate.fromNow();
	}

	return locale.value === "en" ? targetDate.format("MMM D") : targetDate.format("M月D日");
};

const toSquareDetail = async item => {
	if (import.meta.client) {
		// logEvent('site_realfort_consulttype');
	}

	await navigateTo(`/seer/square-detail/${item.id}`);

	await playContent({
		square_id: item.id,
	});
};

provide("formatTime", formatFunction);

provide("formatDate", formatDate);

provide("toSquareDetail", toSquareDetail);

const userStore = useUserStore();

const { isLogin } = storeToRefs(userStore);

const checkColdStartTagsDialog = async () => {
	if (!import.meta.client) return;

	const hasToken = Boolean(localStorage.getItem("token"));
	if (!isLogin.value && !hasToken) return;

	if (!isLogin.value && hasToken) {
		await userStore.refreshLoginStatus();
	}

	if (!isLogin.value) return;

	eventBus.emit(COLD_START_TAGS_TAB_SWITCH_EVENT);
};

const handleLoginSuccess = () => {
	void checkColdStartTagsDialog();
};

const refreshSquareMediaListAfterTagsConfirmed = async () => {
	if (!isLogin.value) return;

	try {
		await refreshPageData();
	} catch (error) {
		console.error("标签提交后刷新媒体广场数据失败:", error);
	}
};

const isPageReload = () => {
	if (!import.meta.client) return false;

	const navigation = performance.getEntriesByType("navigation")?.[0];

	return navigation?.type === "reload";
};

onMounted(() => {
	mediaTracking.trackMediaPageView();

	void checkColdStartTagsDialog();

	eventBus.on(COLD_START_TAGS_CONFIRMED_EVENT, refreshSquareMediaListAfterTagsConfirmed);
	eventBus.on("login-success", handleLoginSuccess);

	if (isPageReload()) {
		refreshNuxtData();
	}

	if (import.meta.client) {
		const savedScrollTop = Number(sessionStorage.getItem(STORAGE_KEY) || 0);

		if (savedScrollTop > 0) {
			requestAnimationFrame(() => {
				window.scrollTo({ top: savedScrollTop, behavior: "auto" });
			});
		}
	}
});

onBeforeUnmount(() => {
	eventBus.off(COLD_START_TAGS_CONFIRMED_EVENT, refreshSquareMediaListAfterTagsConfirmed);
	eventBus.off("login-success", handleLoginSuccess);

	if (import.meta.client) {
		sessionStorage.setItem(STORAGE_KEY, String(window.scrollY || 0));
	}
});

watch(isLogin, (loggedIn, wasLoggedIn) => {
	if (loggedIn && wasLoggedIn === false) {
		void checkColdStartTagsDialog();
	}
});
</script>

<style lang="scss" scoped>
.pc-square {
	padding-bottom: 150px;
	overflow-x: clip;

	.pc-square-toolbar {
		position: relative;

		display: flex;

		align-items: center;

		justify-content: center;

		padding: 24px 0 0;

		:deep(.pc-square-search) {
			width: 680px;
		}

		&__creator-btn {
			position: absolute;

			right: -55px;

			height: 44px;

			padding: 0 24px;

			border: none;

			border-radius: 999px;

			background: #f2af19;

			font-size: 20px;

			line-height: 1;

			color: #ffffff;

			cursor: pointer;

			white-space: nowrap;
		}
	}

	.banner {
		width: 100%;
		height: auto;
		margin: 20px 0;
		box-sizing: border-box;
	}
}
</style>
